import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { EditOutlined } from '@ant-design/icons'
import { Avatar, Button, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import WarningPopover from '../RotaBuilder/common/components/popovers/WarningPopover'
import type { ReconciliationRow } from '../../Reports/types/reconciliation'
import { ReconciliationPayType } from '../../Reports/types/reconciliation'
import {
  formatDisplayName,
  formatMinutes,
  formatTime
} from './reconciliationUtils'

export type GetReconciliationTableColumnsParams = {
  t: (key: string, options?: Record<string, unknown>) => string
  onDontPay: (record: ReconciliationRow) => void
  onEditPayAmount: (record: ReconciliationRow) => void
}

export function getReconciliationTableColumns({
  t,
  onDontPay,
  onEditPayAmount
}: GetReconciliationTableColumnsParams): ColumnsType<ReconciliationRow> {
  const columns: ColumnsType<ReconciliationRow> = [
    {
      title: (
        <LeftmostCellContainer>
          <Text weight="medium">{t('RECONCILIATION_TABLE_STATUS')}</Text>
        </LeftmostCellContainer>
      ),
      key: 'status',
      sorter: true,
      width: 148,
      render: (_, record) => {
        const inner = record.isCanceled ? (
          <ReconciliationTableTag>
            {t('RECONCILIATION_STATUS_CANCELLED')}
          </ReconciliationTableTag>
        ) : record.status === 'reconciled' ? (
          <ReconciliationTableTag color="success">
            {t('RECONCILIATION_STATUS_RECONCILED')}
          </ReconciliationTableTag>
        ) : (
          <ReconciliationTableTag color="orange">
            {t('RECONCILIATION_STATUS_NEEDS_ATTENTION')}
          </ReconciliationTableTag>
        )
        return <LeftmostCellContainer>{inner}</LeftmostCellContainer>
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_DATE')}</Text>,
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      defaultSortOrder: 'descend',
      width: 124,
      render: (date: string) => (
        <CellText>
          <CellPrimaryText>{dayjs(date).format('DD-MM-YYYY')}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_TEAM_MEMBER')}</Text>
      ),
      key: 'teamMember',
      sorter: true,
      width: 220,
      render: (_, record) => {
        const name = formatDisplayName(record.teamMember.name)
        return (
          <CellWithAvatar>
            <AvatarCell>
              <Avatar
                url={record.teamMember.avatarUrl ?? undefined}
                size={22}
              />
            </AvatarCell>
            <CellPrimaryText>{name}</CellPrimaryText>
          </CellWithAvatar>
        )
      }
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_SERVICE_USER')}</Text>
      ),
      key: 'serviceUser',
      width: 220,
      render: (_, record) => {
        if (!record.serviceUser)
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        const name = formatDisplayName(record.serviceUser.name)
        return (
          <CellWithAvatar>
            <AvatarCell>
              <Avatar
                url={record.serviceUser.avatarUrl ?? undefined}
                size={22}
              />
            </AvatarCell>
            <CellPrimaryText>{name}</CellPrimaryText>
          </CellWithAvatar>
        )
      }
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}</Text>
      ),
      key: 'eventType',
      sorter: true,
      width: 220,
      render: (_, record) => (
        <CellText>
          <CellPrimaryText>{record.eventType.name}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_PLANNED_TIME_DURATION')}
        </MultilineHeaderText>
      ),
      key: 'plannedTime',
      sorter: true,
      width: 200,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => (
        <TimeStack>
          <CellPrimaryText>
            {formatTime(record.plannedTime.startTime)}–
            {formatTime(record.plannedTime.endTime)}
          </CellPrimaryText>
          <CellSecondaryText>
            {formatMinutes(record.plannedTime.durationMinutes)}
          </CellSecondaryText>
        </TimeStack>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_ACTUAL_TIME_DURATION')}
        </MultilineHeaderText>
      ),
      key: 'actualTime',
      width: 200,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => {
        if (record.isCanceled)
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_EVENT_CANCELED')}</Text>
              </DangerText>
            </CellText>
          )
        if (!record.actualTime)
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_EVENT_MISSED')}</Text>
              </DangerText>
            </CellText>
          )
        return (
          <TimeStack>
            <CellPrimaryText>
              {formatTime(record.actualTime.startTime)}–
              {formatTime(record.actualTime.endTime)}
            </CellPrimaryText>
            <CellSecondaryText>
              {formatMinutes(record.actualTime.durationMinutes)}
            </CellSecondaryText>
          </TimeStack>
        )
      }
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_DURATION_DISCREPANCY')}
        </MultilineHeaderText>
      ),
      key: 'durationDiscrepancy',
      width: 140,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => {
        if (record.isCanceled) return null
        const diff = record.durationDiscrepancy
        if (diff === null)
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        return (
          <CellText>
            <CellPrimaryText>
              {diff > 0 ? '+' : ''}
              {diff}m
            </CellPrimaryText>
          </CellText>
        )
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_WARNING')}</Text>,
      key: 'warningCount',
      width: 100,
      render: (_, record) =>
        record.warningCount === 0 ? (
          <CellText>
            <CellPrimaryText>-</CellPrimaryText>
          </CellText>
        ) : record.warnings.length > 0 ? (
          <WarningPopover warningsData={record.warnings}>
            <WarningCountTrigger>
              <CellPrimaryText>{record.warningCount}</CellPrimaryText>
            </WarningCountTrigger>
          </WarningPopover>
        ) : (
          <CellText>
            <CellPrimaryText>{record.warningCount}</CellPrimaryText>
          </CellText>
        )
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_PAID_TIME')}</Text>,
      key: 'paidTime',
      width: 130,
      render: (_, record) => {
        const rec = record.reconciliation
        if (!rec)
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        if (rec.payType === ReconciliationPayType.NONE) {
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_NOT_PAID')}</Text>
              </DangerText>
            </CellText>
          )
        }
        return (
          <CellText>
            <CellPrimaryText>{formatMinutes(rec.paidMinutes)}</CellPrimaryText>
          </CellText>
        )
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_PAY_RATE')}</Text>,
      key: 'payRate',
      width: 130,
      render: (_, record) => {
        const rate = record.payRate
        if (!rate)
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        const unit =
          rate.unit === 'hour'
            ? t('RECONCILIATION_RATE_GBP_HOUR')
            : t('RECONCILIATION_RATE_GBP_SHIFT')
        return (
          <PayRateStack>
            <CellPrimaryText>
              {rate.amount} {unit}
            </CellPrimaryText>
            {rate.label && (
              <CellSecondaryText>{rate.label}</CellSecondaryText>
            )}
          </PayRateStack>
        )
      }
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_PAY_AMOUNT')}</Text>
      ),
      key: 'payAmount',
      width: 150,
      render: (_, record) => {
        const rec = record.reconciliation
        const editButton = (
          <EditIconButton
            onClick={(e) => {
              e.stopPropagation()
              onEditPayAmount(record)
            }}
          >
            <EditOutlined />
          </EditIconButton>
        )
        if (!rec)
          return (
            <PayAmountCell>
              {editButton}
              <CellPrimaryText>-</CellPrimaryText>
            </PayAmountCell>
          )
        if (rec.payType === ReconciliationPayType.NONE) {
          return (
            <PayAmountCell>
              {editButton}
              <CellPrimaryText>-</CellPrimaryText>
            </PayAmountCell>
          )
        }
        return (
          <PayAmountCell>
            {editButton}
            <CellPrimaryText>
              {rec.payAmount} {t('RECONCILIATION_GBP_SUFFIX')}
            </CellPrimaryText>
          </PayAmountCell>
        )
      }
    },
    {
      title: '',
      key: 'dontPay',
      width: 120,
      render: (_, record) => (
        <Button
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            onDontPay(record)
          }}
        >
          {t('RECONCILIATION_PAY_TYPE_DONT_PAY')}
        </Button>
      )
    }
  ]

  return columns
}

const MultilineHeaderText = styled(Text)`
  white-space: pre-line;
`

const ReconciliationTableTag = styled(Tag)`
  padding: 4px 8px;
  font-size: 12px;
`

const LeftmostCellContainer = styled.div`
  padding-left: 12px;
`

const CellText = styled.div``

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellSecondaryText = styled(Text).attrs({
  level: 2,
  weight: 'medium'
})`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const AvatarCell = styled.div`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-avatar {
    width: 22px !important;
    height: 22px !important;
    line-height: 22px !important;
    flex-shrink: 0;
  }

  .ant-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TimeStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PayRateStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MutedText = styled.span`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DangerText = styled.span`
  color: #f5222d;
`

const WarningCountTrigger = styled.span`
  cursor: pointer;
`

const ActionCellWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

const PayAmountCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EditIconButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 14px;
`
